import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { useTranslation } from '../i18n';
import { investmentCriteriaAPI } from '../api';
import Header from '../components/Header';
import AuthPrompt from '../components/AuthPrompt';
import { Target, Save, RotateCcw, TrendingUp, Home, DollarSign, HelpCircle, Loader2, Check, AlertCircle, Info, Percent, Calculator, Building2, Sparkles, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { FormattedCurrencyInput } from '../components/FormattedInput';
import { parseFormattedNumber } from '../utils/formatNumber';

// Tooltips for each field
const TOOLTIPS = {
  en: {
    min_net_profit: { title: 'Minimum Net Profit', description: 'The minimum dollar profit you want to make after all expenses on a flip.', typical: '$25,000 - $50,000' },
    min_profit_margin: { title: 'Minimum Profit Margin', description: 'Net profit as a percentage of ARV. Higher margins provide more safety.', typical: '12% - 20%' },
    min_roi: { title: 'Minimum ROI', description: 'Return on Investment - profit divided by total cash invested.', typical: '15% - 30%' },
    min_monthly_cash_flow: { title: 'Minimum Monthly Cash Flow', description: 'Monthly income remaining after all expenses for rentals.', typical: '$200 - $500' },
    min_cap_rate: { title: 'Minimum Cap Rate', description: 'Cap Rate measures return before financing. NOI / Purchase Price.', typical: '6% - 10%' },
    min_cash_on_cash: { title: 'Minimum Cash-on-Cash Return', description: 'Annual cash flow divided by total cash invested.', typical: '8% - 15%' },
    min_dscr: { title: 'Minimum DSCR', description: 'Debt Service Coverage Ratio - NOI divided by debt payments. Banks require 1.2+.', typical: '1.2 - 1.5' },
    min_cash_flow_after_refi: { title: 'Min Cash Flow After Refinance', description: 'Monthly cash flow after refinancing into permanent debt.', typical: '$150 - $300' },
    max_cash_left_in_deal: { title: 'Maximum Cash Left in Deal', description: 'Maximum investment remaining after refinancing. BRRRR targets $0.', typical: '$0 - $30,000' },
    target_refi_ltv: { title: 'Target Refinance LTV', description: 'Loan-to-Value ratio at refinance. Higher LTV = more cash out.', typical: '70% - 80%' }
  },
  es: {
    min_net_profit: { title: 'Ganancia Neta Mínima', description: 'La ganancia mínima después de gastos en un flip.', typical: '$25,000 - $50,000' },
    min_profit_margin: { title: 'Margen de Ganancia Mínimo', description: 'Ganancia neta como porcentaje del ARV.', typical: '12% - 20%' },
    min_roi: { title: 'ROI Mínimo', description: 'Retorno sobre la Inversión.', typical: '15% - 30%' },
    min_monthly_cash_flow: { title: 'Flujo de Caja Mensual Mínimo', description: 'Ingreso mensual después de gastos para alquileres.', typical: '$200 - $500' },
    min_cap_rate: { title: 'Cap Rate Mínimo', description: 'El Cap Rate mide el retorno antes del financiamiento.', typical: '6% - 10%' },
    min_cash_on_cash: { title: 'Retorno Cash-on-Cash Mínimo', description: 'Flujo de caja anual dividido por inversión.', typical: '8% - 15%' },
    min_dscr: { title: 'DSCR Mínimo', description: 'Ratio de Cobertura de Servicio de Deuda.', typical: '1.2 - 1.5' },
    min_cash_flow_after_refi: { title: 'Flujo Después de Refinanciar', description: 'Flujo mensual después de refinanciar.', typical: '$150 - $300' },
    max_cash_left_in_deal: { title: 'Máximo Efectivo en el Trato', description: 'Inversión restante después de refinanciar.', typical: '$0 - $30,000' },
    target_refi_ltv: { title: 'LTV Objetivo de Refinanciamiento', description: 'Loan-to-Value al refinanciar.', typical: '70% - 80%' }
  }
};

// CHEKSTE Recommended Defaults for Investors
const RECOMMENDED_DEFAULTS = {
  flip: {
    min_net_profit: 30000,
    min_profit_margin: 15,
    min_roi: 20
  },
  rental: {
    min_monthly_cash_flow: 250,
    min_cap_rate: 7,
    min_cash_on_cash: 10,
    min_dscr: 1.2
  },
  brrrr: {
    min_cash_flow_after_refi: 200,
    max_cash_left_in_deal: 25000,
    target_refi_ltv: 75
  }
};

const DEFAULT_CRITERIA = RECOMMENDED_DEFAULTS;

const TooltipButton = ({ field, lang }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltips = TOOLTIPS[lang] || TOOLTIPS.en;
  const tooltip = tooltips[field];
  
  if (!tooltip) return null;
  
  return (
    <div className="relative inline-block">
      <button
        type="button"
        className="text-slate-400 hover:text-blue-500 transition-colors ml-1 -my-2 p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center touch-manipulation"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-slate-800 text-white rounded-lg shadow-xl text-sm">
          <div className="font-semibold mb-1">{tooltip.title}</div>
          <p className="text-slate-300 text-xs mb-2">{tooltip.description}</p>
          <div className="text-xs text-blue-300">
            {lang === 'en' ? 'Typical:' : 'Típico:'} {tooltip.typical}
          </div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800"></div>
        </div>
      )}
    </div>
  );
};

const CriteriaPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { lang } = useTranslation();
  
  const [criteria, setCriteria] = useState(DEFAULT_CRITERIA);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [savedCriteria, setSavedCriteria] = useState(null);
  const [showDefaultsApplied, setShowDefaultsApplied] = useState(false);

  // Load user's criteria when user is available
  useEffect(() => {
    const loadCriteria = async () => {
      try {
        const response = await investmentCriteriaAPI.getCriteria();
        if (response.data?.criteria) {
          setCriteria(response.data.criteria);
          setSavedCriteria(response.data.criteria);
        }
      } catch (error) {
        console.error('Failed to load criteria:', error);
        // Use defaults
        setSavedCriteria(DEFAULT_CRITERIA);
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      loadCriteria();
    } else {
      // No user, stop loading but don't redirect immediately
      // This allows the page to show a login prompt instead
      setLoading(false);
    }
  }, [user]);

  const handleFieldChange = (category, field, value) => {
    setCriteria(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await investmentCriteriaAPI.updateCriteria(criteria);
      setSavedCriteria(criteria);
      setHasChanges(false);
      setShowDefaultsApplied(false);
      toast.success(
        lang === 'en' 
          ? 'Your investment criteria were updated!' 
          : '¡Tus criterios de inversión fueron actualizados!'
      );
    } catch (error) {
      console.error('Failed to save criteria:', error);
      toast.error(
        lang === 'en' 
          ? 'Failed to save criteria' 
          : 'Error al guardar criterios'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setCriteria(DEFAULT_CRITERIA);
    setHasChanges(true);
    setShowDefaultsApplied(false);
    toast.info(
      lang === 'en' 
        ? 'Reset to default values. Click Save to apply.' 
        : 'Valores por defecto restaurados. Haz clic en Guardar para aplicar.'
    );
  };

  const handleApplySuggestedDefaults = () => {
    setCriteria(RECOMMENDED_DEFAULTS);
    setHasChanges(true);
    setShowDefaultsApplied(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container mx-auto px-4 pt-24 pb-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container mx-auto px-4 pt-24 pb-12">
          <AuthPrompt
            icon={Target}
            title={lang === 'en' ? 'Sign In to Access Your Criteria' : 'Inicia Sesión para Acceder a tus Criterios'}
            description={lang === 'en' 
              ? 'Create a free account or sign in to set your personal investment thresholds and get customized deal recommendations.'
              : 'Crea una cuenta gratuita o inicia sesión para establecer tus umbrales de inversión y obtener recomendaciones personalizadas.'}
            lang={lang}
            redirectAfterAuth="/criteria"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <div className="container mx-auto px-4 pt-24 pb-12 max-w-4xl">
        {/* Back Navigation */}
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors group min-h-[44px] py-2 -ml-1 px-1 touch-manipulation"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          {lang === 'en' ? 'Back' : 'Volver'}
        </button>
        
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Target className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800" data-testid="criteria-page-title">
                {lang === 'en' ? 'My Investment Criteria' : 'Mis Criterios de Inversión'}
              </h1>
              <p className="text-slate-500">
                {lang === 'en' 
                  ? 'Set your minimum deal requirements for personalized recommendations' 
                  : 'Establece tus requisitos mínimos para recomendaciones personalizadas'}
              </p>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3" data-testid="criteria-info-banner">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">
              {lang === 'en' ? 'How It Works' : 'Cómo Funciona'}
            </p>
            <p>
              {lang === 'en' 
                ? 'Your criteria determine whether a deal receives a PASS, NEGOTIATE, or NO PASS recommendation. Deals that meet all criteria get a PASS. Deals close to criteria get NEGOTIATE.'
                : 'Tus criterios determinan si un trato recibe una recomendación de PASAR, NEGOCIAR, o NO PASAR. Los tratos que cumplen todos los criterios obtienen PASAR.'}
            </p>
          </div>
        </div>

        {/* Apply Suggested Defaults Button */}
        <div className="mb-6" data-testid="suggested-defaults-section">
          <button
            onClick={handleApplySuggestedDefaults}
            className="w-full sm:w-auto px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-teal-600 flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
            data-testid="apply-defaults-btn"
          >
            <Sparkles className="w-5 h-5" />
            {lang === 'en' ? 'Apply Suggested Defaults' : 'Aplicar valores recomendados'}
          </button>
          <p className="mt-2 text-xs text-slate-500">
            {lang === 'en' 
              ? 'Use CHEKSTE\'s recommended thresholds for beginner investors'
              : 'Usa los umbrales recomendados de CHEKSTE para inversores principiantes'}
          </p>
        </div>

        {/* Defaults Applied Message */}
        {showDefaultsApplied && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3" data-testid="defaults-applied-message">
            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-700">
              <p className="font-medium">
                {lang === 'en' 
                  ? 'Recommended investor thresholds applied. Review and click Save to confirm.'
                  : 'Se aplicaron criterios recomendados para inversionistas. Revísalos y presiona Guardar para confirmar.'}
              </p>
            </div>
          </div>
        )}

        {/* Flip Criteria */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6" data-testid="flip-criteria-section">
          <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-amber-50">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-500" />
              <h2 className="font-semibold text-slate-800">
                {lang === 'en' ? 'Fix & Flip Criteria' : 'Criterios para Fix & Flip'}
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {lang === 'en' 
                ? 'Minimum requirements for flip investments' 
                : 'Requisitos mínimos para inversiones de volteo'}
            </p>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {/* Min Net Profit */}
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-2">
                <DollarSign className="w-4 h-4 mr-1 text-slate-400" />
                {lang === 'en' ? 'Min Net Profit' : 'Ganancia Neta Mín'}
                <TooltipButton field="min_net_profit" lang={lang} />
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">$</span>
                <FormattedCurrencyInput
                  value={criteria.flip?.min_net_profit || ''}
                  onChange={(val) => handleFieldChange('flip', 'min_net_profit', parseFormattedNumber(val) || 0)}
                  prefix={null}
                  className="w-full pl-7 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="30000"
                  data-testid="flip-min-profit-input"
                />
              </div>
            </div>
            
            {/* Min Profit Margin */}
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-2">
                <Percent className="w-4 h-4 mr-1 text-slate-400" />
                {lang === 'en' ? 'Min Profit Margin' : 'Margen Mínimo'}
                <TooltipButton field="min_profit_margin" lang={lang} />
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={criteria.flip?.min_profit_margin || ''}
                  onChange={(e) => handleFieldChange('flip', 'min_profit_margin', parseFloat(e.target.value) || 0)}
                  className="w-full pr-8 pl-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="15"
                  data-testid="flip-min-margin-input"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400">%</span>
              </div>
            </div>
            
            {/* Min ROI */}
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-2">
                <Calculator className="w-4 h-4 mr-1 text-slate-400" />
                {lang === 'en' ? 'Min ROI' : 'ROI Mínimo'}
                <TooltipButton field="min_roi" lang={lang} />
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={criteria.flip?.min_roi || ''}
                  onChange={(e) => handleFieldChange('flip', 'min_roi', parseFloat(e.target.value) || 0)}
                  className="w-full pr-8 pl-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="20"
                  data-testid="flip-min-roi-input"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Rental Criteria */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6" data-testid="rental-criteria-section">
          <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center gap-2">
              <Home className="w-5 h-5 text-blue-500" />
              <h2 className="font-semibold text-slate-800">
                {lang === 'en' ? 'Rental Criteria' : 'Criterios para Alquiler'}
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {lang === 'en' 
                ? 'Minimum requirements for rental investments' 
                : 'Requisitos mínimos para inversiones de alquiler'}
            </p>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Min Monthly Cash Flow */}
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-2">
                {lang === 'en' ? 'Min Cash Flow/mo' : 'Flujo Mensual Mín'}
                <TooltipButton field="min_monthly_cash_flow" lang={lang} />
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">$</span>
                <FormattedCurrencyInput
                  value={criteria.rental?.min_monthly_cash_flow ?? ''}
                  onChange={(val) => handleFieldChange('rental', 'min_monthly_cash_flow', parseFormattedNumber(val) || 0)}
                  prefix={null}
                  className="w-full pl-7 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="250"
                  data-testid="rental-min-cashflow-input"
                />
              </div>
            </div>
            
            {/* Min Cap Rate */}
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-2">
                {lang === 'en' ? 'Min Cap Rate' : 'Cap Rate Mín'}
                <TooltipButton field="min_cap_rate" lang={lang} />
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  value={criteria.rental?.min_cap_rate ?? ''}
                  onChange={(e) => handleFieldChange('rental', 'min_cap_rate', parseFloat(e.target.value) || 0)}
                  className="w-full pr-8 pl-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="7"
                  data-testid="rental-min-caprate-input"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400">%</span>
              </div>
            </div>
            
            {/* Min Cash-on-Cash */}
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-2">
                {lang === 'en' ? 'Min Cash-on-Cash' : 'Cash-on-Cash Mín'}
                <TooltipButton field="min_cash_on_cash" lang={lang} />
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  value={criteria.rental?.min_cash_on_cash || ''}
                  onChange={(e) => handleFieldChange('rental', 'min_cash_on_cash', parseFloat(e.target.value) || 0)}
                  className="w-full pr-8 pl-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="10"
                  data-testid="rental-min-coc-input"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400">%</span>
              </div>
            </div>
            
            {/* Min DSCR */}
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-2">
                {lang === 'en' ? 'Min DSCR' : 'DSCR Mínimo'}
                <TooltipButton field="min_dscr" lang={lang} />
              </label>
              <input
                type="number"
                step="0.05"
                value={criteria.rental?.min_dscr ?? ''}
                onChange={(e) => handleFieldChange('rental', 'min_dscr', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="1.25"
                data-testid="rental-min-dscr-input"
              />
            </div>
          </div>
        </div>

        {/* BRRRR Criteria */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-8" data-testid="brrrr-criteria-section">
          <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" />
              <h2 className="font-semibold text-slate-800">
                {lang === 'en' ? 'BRRRR Strategy Criteria' : 'Criterios para Estrategia BRRRR'}
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {lang === 'en' 
                ? 'Buy, Rehab, Rent, Refinance, Repeat' 
                : 'Comprar, Rehabilitar, Alquilar, Refinanciar, Repetir'}
            </p>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {/* Min Cash Flow After Refi */}
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-2">
                {lang === 'en' ? 'Min Cash Flow After Refi' : 'Flujo Post-Refi Mín'}
                <TooltipButton field="min_cash_flow_after_refi" lang={lang} />
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">$</span>
                <FormattedCurrencyInput
                  value={criteria.brrrr?.min_cash_flow_after_refi || ''}
                  onChange={(val) => handleFieldChange('brrrr', 'min_cash_flow_after_refi', parseFormattedNumber(val) || 0)}
                  prefix={null}
                  className="w-full pl-7 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="200"
                  data-testid="brrrr-min-cashflow-input"
                />
              </div>
            </div>
            
            {/* Max Cash Left in Deal */}
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-2">
                {lang === 'en' ? 'Max Cash Left in Deal' : 'Máx. Efectivo en Trato'}
                <TooltipButton field="max_cash_left_in_deal" lang={lang} />
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">$</span>
                <FormattedCurrencyInput
                  value={criteria.brrrr?.max_cash_left_in_deal || ''}
                  onChange={(val) => handleFieldChange('brrrr', 'max_cash_left_in_deal', parseFormattedNumber(val) || 0)}
                  prefix={null}
                  className="w-full pl-7 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="20000"
                  data-testid="brrrr-max-cash-input"
                />
              </div>
            </div>
            
            {/* Target Refi LTV */}
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-2">
                {lang === 'en' ? 'Target Refi LTV' : 'LTV Objetivo Refi'}
                <TooltipButton field="target_refi_ltv" lang={lang} />
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={criteria.brrrr?.target_refi_ltv || ''}
                  onChange={(e) => handleFieldChange('brrrr', 'target_refi_ltv', parseFloat(e.target.value) || 0)}
                  className="w-full pr-8 pl-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="75"
                  data-testid="brrrr-refi-ltv-input"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3" data-testid="criteria-actions">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-sm"
            data-testid="save-criteria-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {lang === 'en' ? 'Saving...' : 'Guardando...'}
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {lang === 'en' ? 'Save Criteria' : 'Guardar Criterios'}
              </>
            )}
          </button>
          <button
            onClick={handleReset}
            className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 flex items-center justify-center gap-2 transition-all"
            data-testid="reset-criteria-btn"
          >
            <RotateCcw className="w-5 h-5" />
            {lang === 'en' ? 'Reset to Defaults' : 'Restablecer'}
          </button>
        </div>

        {/* Unsaved Changes Warning */}
        {hasChanges && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-700 text-sm" data-testid="unsaved-warning">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {lang === 'en' 
              ? 'You have unsaved changes. Click "Save Criteria" to apply them.'
              : 'Tienes cambios sin guardar. Haz clic en "Guardar Criterios" para aplicarlos.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default CriteriaPage;
